import React from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import { useSection, useSiteContent, useSeo } from "@/contexts/SiteContentContext";

export default function About() {
  const c = useSection("about");
  const { faq } = useSiteContent();
  useSeo(c.seo_title, c.meta_description);
  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white py-20 border-b-4 border-[#E63946]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946]">{c.overline || "About"}</div>
          <h1 data-testid="about-title" className="font-display text-5xl lg:text-6xl uppercase tracking-tighter mt-4">{c.title || "Built for Canadian Baseball"}</h1>
          <p className="mt-6 text-lg text-white/80 max-w-3xl leading-relaxed">
            {c.subtitle || "CBR Prospects is the central platform powering Canadian Baseball Rankings — built to identify, measure, evaluate, rank, and place Canadian amateur baseball talent at the next level."}
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 space-y-12">
          <div>
            <div className="label-overline text-[#E63946]">Mission</div>
            <h2 className="font-display text-3xl uppercase tracking-tight mt-2">Verified. Permanent. Trusted.</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              {c.body || "Every metric on CBR Prospects is captured by certified CBR staff at official events. Every evaluation comes from accredited evaluators using the traditional 20-80 scouting scale. Every ranking is built on transparent, weighted criteria. No marketing claims. No paid placements. Just the data."}
            </p>
          </div>

          {c.image_url && <img src={c.image_url} alt="" data-testid="about-image" className="w-full rounded-sm border border-slate-200 object-cover max-h-96" />}

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Identify", d: "Discover Canadian talent across every province and graduation class through CBR camps and showcases." },
              { n: "02", t: "Measure", d: "Capture verified metrics: exit velocity, 60-yard, pop time, bullpen velocity, spin rate." },
              { n: "03", t: "Evaluate", d: "Traditional 20-80 scouting reports from accredited CBR evaluators at every event." },
              { n: "04", t: "Rank", d: "Combined CBR Overall Rating blending verified metrics, evaluator scores, and development trajectory." },
              { n: "05", t: "Promote", d: "Permanent recruiting profiles shared directly with college scouts and recruiters." },
              { n: "06", t: "Place", d: "From provincial showcases to CBR Nation selection to NCAA commitments and pro contracts." },
            ].map((s) => (
              <div key={s.n} className="border border-slate-200 p-6">
                <div className="font-data text-3xl text-[#E63946]">{s.n}</div>
                <div className="font-display text-lg uppercase tracking-tight mt-2">{s.t}</div>
                <p className="text-sm text-slate-600 mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {faq && faq.items && faq.items.length > 0 && (
        <section data-testid="faq-section" className="bg-[#F8F9FA] py-16 border-t border-slate-200">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="label-overline text-[#E63946]">{faq.overline || "FAQ"}</div>
            <h2 className="font-display text-3xl lg:text-4xl uppercase tracking-tighter mt-2">{faq.title || "Frequently Asked Questions"}</h2>
            {faq.subtitle && <p className="text-slate-600 mt-3">{faq.subtitle}</p>}
            <div className="mt-8 space-y-3">
              {faq.items.map((q, i) => (
                <details key={q.id || i} data-testid={`faq-q-${i}`} className="group bg-white border border-slate-200 rounded-sm p-4">
                  <summary className="font-semibold cursor-pointer list-none flex items-center justify-between gap-3">
                    {q.question}
                    <span className="text-[#E63946] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{q.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
